import type { CaveDef, CaveEndDef, CaveLinkDef } from '../scenes/CaveWorld';
import { EMBERDEEP } from './emberdeep';
import { GLOWMIRE } from './glowmire';
import { VERDANT } from './verdant';

// The ends of the caves, per world: light spilling in, lichen near the mouth, boulders.
export const VERDANT_MOUTH: Omit<CaveEndDef, 'height'> = {
  leak: [0.55, 0.5, 0.42],
  glow: [0.7, 0.46, 0.18],
  rocks: { light: [0.5, 0.47, 0.42], dark: [0.3, 0.29, 0.26] },
};
export const GLOWMIRE_MOUTH: Omit<CaveEndDef, 'height'> = {
  leak: [0.3, 0.5, 0.95],
  glow: [0.16, 0.55, 1.3],
  rocks: { light: [0.13, 0.12, 0.17], dark: [0.06, 0.08, 0.09], glow: [0.05, 0.3, 0.42] },
};
export const EMBERDEEP_MOUTH: Omit<CaveEndDef, 'height'> = {
  leak: [1.1, 0.4, 0.1],
  // Near Emberdeep the "lichen" is ember veins in the rock.
  glow: [1.7, 0.3, 0.02],
  rocks: { light: [0.12, 0.1, 0.09], dark: [0.05, 0.04, 0.035], glow: [0.6, 0.15, 0.03] },
};
export const UMBRAL_MOUTH: Omit<CaveEndDef, 'height'> = {
  leak: [0.04, 0.05, 0.07],
  glow: [0.12, 0.18, 0.34],
  rocks: { light: [0.3, 0.29, 0.27], dark: [0.14, 0.14, 0.15] },
};

/** What to know about the caves (the first time in a cave world). */
export const CAVE_HINTS = [
  'A ring of stones is a cave mouth: walk in. The stair spirals down to a cave through the rock.',
  'In a cave every wall is a floor, and gravity is light: walk up the walls, and any jump carries you across.',
  'Some caverns have no gravity at all: Space and C float you up and down, Q and E roll you round.',
];

/**
 * From a meadow in Verdant a spiral stair winds down into the rock, to a long cave that
 * comes out up another stair in Glowmire's marsh. (Axes and heights come from a search
 * for dry, flat ground at both ends.)
 */
const VERDANT_GLOWMIRE: CaveLinkDef = {
  from: 0,
  to: 1,
  axis: [0.098, 0.7809, -0.617],
  spiralGap: 260,
  tunnel: { tubeRadius: 4, helixRadius: 24, pitchDeg: 24, depth: 10, phase: 0.6 },
  a: { height: 60, ...VERDANT_MOUTH },
  b: { height: 8.5, ...GLOWMIRE_MOUTH },
};

/** On from Glowmire's marsh, down again and up onto Emberdeep's black hills. */
const GLOWMIRE_EMBERDEEP: CaveLinkDef = {
  from: 1,
  to: 2,
  axis: [-0.2942, 0.5257, 0.7982],
  spiralGap: 260,
  tunnel: { tubeRadius: 4, helixRadius: 22, pitchDeg: 24, depth: 10, phase: 2.2 },
  a: { height: 10.7, ...GLOWMIRE_MOUTH },
  b: { height: 43.1, ...EMBERDEEP_MOUTH },
};

/** Verdant and Glowmire joined by one cave. */
export const VERDANT_TO_GLOWMIRE: CaveDef = {
  id: 'verdant-glowmire',
  name: 'The Glowing Stair',
  hollows: [VERDANT, GLOWMIRE],
  links: [VERDANT_GLOWMIRE],
  hints: CAVE_HINTS,
};

/** Three worlds on one walk: sunlit Verdant, glowing Glowmire, and Emberdeep over its lava sea. */
export const THREE_WORLDS: CaveDef = {
  id: 'three-worlds',
  name: 'The Long Way Down',
  hollows: [VERDANT, GLOWMIRE, EMBERDEEP],
  links: [VERDANT_GLOWMIRE, GLOWMIRE_EMBERDEEP],
  hints: [...CAVE_HINTS, 'In Glowmire, look for the second ring of stones: the way on to Emberdeep.'],
};
